// ProfileInfo.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import UniversalContainer from '../component/UniversalContainer.jsx';
import { BadgeType as JoinProgressBadgeType } from '../component/JoinProgressBadge.jsx';

import UserStatusIndicator from '../container/UserStatusIndicator.js';
import FollowButton from '../container/FollowButton.js';
import SubscribeButton from '../container/SubscribeButton.js';
import ProfileInfoBadges from '../container/ProfileInfoBadges.js';
import UserLevelBadge from '../container/UserLevelBadge.js';
import LinkSubstring from '../container/LinkSubstring.js';
import UploadStoryFileInput from '../container/UploadStoryFileInput.js';
import LocationBadge from '../container/LocationBadge.js';
import JoinProgressBadge from '../container/JoinProgressBadge.js';
import ModalView from '../container/ModalView.js';

import withFetchTranslation from '../resource/withFetchTranslation.js';
import formatNumber from '../resource/formatNumber.js';
import { wordsFilter } from '../resource/wordsFilter.js';
import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import {
  ManageProfileType,
  UserMoreFeature,
} from '../resource/profileConstants.js';
import { getOnlineStatusWording } from '../resource/onlineStatus.js';
import {
  LIVESTREAM_TYPE_DOT,
  ONLINE_TYPE_BIGGER_DOT,
  ONLINE_TYPE_DOT,
} from '../resource/userStatusIndicatorConstants.js';
import getCanShare from '../resource/getCanShare.js';
import { isClient, getIsOnMobile } from '../resource/getUserAgent.js';
import { getIsChinaPartner } from '../resource/partner.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { color, textColor } from '../style/variables.js';
import media from '../style/media.js';

import AddIconSource from '../../img/ic_add_white_xs.svg';
import ChatIconSource from '../../img/btn_chat_24.svg';
import LinkIconSource from '../../img/ic-link.svg';
import ShareIconSource from '../../img/ic-share-20.svg';
import CreatorIconSource from '../../img/ic_badge_creator.svg';
import MoreIconSource from '../../img/ic_topbar_more.svg';

const AVATAR_SIZE = 110;
const MOBILE_AVATAR_SIZE = 64;

const canShare = getCanShare();
const isMobile = getIsOnMobile();
const isChinaPartner = getIsChinaPartner();

export class ProfileInfo extends React.PureComponent {
  state = {
    shouldShowTranslatedBiography: true,
    isBiographyTruncated: true,
  };
  trackUserClick = ({ buttonId }) =>
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        buttonId,
      },
    });
  setBiographyRef = element => {
    const { biography, originalBiography } = this.props;
    const { shouldShowTranslatedBiography } = this.state;
    const displayBiography = shouldShowTranslatedBiography
      ? biography
      : originalBiography;
    if (element && displayBiography) {
      this.setState({
        isBiographyTruncated: element.clientHeight !== element.scrollHeight,
      });
    }
  };
  fetchUserIsBlocked = () => {
    const { userId, isMeProfile, fetchUserIsBlocked } = this.props;
    if (userId && !isMeProfile) {
      return fetchUserIsBlocked({ userId });
    }
  };
  fetchShortLink = () => {
    const { shareLink, shortShareLink, fetchShortLink, userId, meId } =
      this.props;
    if (shareLink && !shortShareLink) {
      fetchShortLink({
        location: shareLink,
        utmQueries: {
          utm_source: 'share',
          utm_content: `profile_${userId}`,
          utm_campaign: meId ? `userid_${meId}` : '',
        },
      });
    }
  };
  fetchMeCreator = () => {
    const { meId, isMeProfile, fetchMeCreator } = this.props;
    if (meId && isMeProfile) {
      return fetchMeCreator();
    }
  };
  fetchUser = () => {
    const { userId, fetchUser } = this.props;
    if (userId) {
      return fetchUser({ userId });
    }
    return;
  };
  fetchFeedsTotalCount = () => {
    const {
      intersectedFeedName,
      userId,
      meId,
      postCount,
      filters,
      sorts,
      isMeProfile,
      fetchFeedsTotalCount,
    } = this.props;
    const usingUserId = meId && isMeProfile ? meId : userId;
    if (!usingUserId || postCount) {
      return;
    }
    return fetchFeedsTotalCount({
      type: intersectedFeedName,
      filters,
      sorts,
    });
  };
  fetchFollowStatus = () => {
    const {
      userId,
      meId,
      followerCount,
      followingCount,
      isMeProfile,
      isFollowing,
      fetchUserIsFollowing,
      fetchFollowersTotalCount,
      fetchFollowingTotalCount,
      fetchUserFollowersTotalCount,
      fetchUserFollowingTotalCount,
    } = this.props;
    if (userId && !isMeProfile) {
      if (isFollowing == null) {
        fetchUserIsFollowing({ userId });
      }
      if (!followerCount) {
        fetchUserFollowersTotalCount({ userId });
      }
      if (!followingCount) {
        fetchUserFollowingTotalCount({ userId });
      }
    }
    if (meId && isMeProfile) {
      if (!followerCount) {
        fetchFollowersTotalCount();
      }
      if (!followingCount) {
        fetchFollowingTotalCount();
      }
    }
    return;
  };
  sendFollowUser = () => {
    const { userId, sendFollowUser } = this.props;
    if (userId) {
      this.trackUserClick({
        buttonId: ButtonId.All.ButtonFollow,
      });
      return sendFollowUser({ userId });
    }
  };
  sendUnfollowUser = () => {
    const { userId, sendUnfollowUser } = this.props;
    if (userId) {
      this.trackUserClick({
        buttonId: ButtonId.All.ButtonUnfollow,
      });
      return sendUnfollowUser({ userId });
    }
  };
  sendChatStart = () => {
    const { userId, sendChatStart } = this.props;
    if (userId) {
      this.trackUserClick({
        buttonId: ButtonId.All.ButtonChat,
      });
      return sendChatStart({ userId });
    }
  };
  copyProfileLink = () => {
    const { shareLink, shortShareLink, t, copyTextWithToastr } = this.props;
    this.trackUserClick({
      buttonId: ButtonId.All.ButtonShare,
    });
    copyTextWithToastr({
      copiedText: shortShareLink || shareLink || window.location.href,
      successfulText: t('copy_to_clipboard_successfully'),
      failedText: t('copy_to_clipboard_failed'),
    });
  };
  shareProfile = () => {
    const { username, shareLink, shortShareLink, t } = this.props;
    const shareText = t('title_share_flix_link', { username });
    this.trackUserClick({
      buttonId: ButtonId.All.ButtonShare,
    });
    navigator.share({
      title: 'SWAG',
      text: shareText,
      url: shortShareLink || shareLink || window.location.href,
    });
  };
  renderOnlineStatusWording = () => {
    const {
      userId,
      meId,
      isMeProfile,
      isOnline,
      isLiveStreaming,
      lastModifiedAtUnix,
      t,
    } = this.props;
    return (
      <UserOnlineStatusWording
        isOnline={isOnline}
        isLiveStreaming={isLiveStreaming}
      >
        <UserStatusIndicator
          userId={isMeProfile ? meId : userId}
          liveStreamType={LIVESTREAM_TYPE_DOT}
          livestreamSize={12}
          onlineType={ONLINE_TYPE_DOT}
          shouldShowLiveStream
          shouldShowOnline
          shouldShowOffline
        />
        <div>
          {isLiveStreaming
            ? t('title_streaming')
            : isOnline
            ? t('online_state_online')
            : getOnlineStatusWording({ lastModifiedAtUnix, i18nFn: t })}
        </div>
      </UserOnlineStatusWording>
    );
  };
  renderUserStatusIndicator = () => {
    const { userId, isMeProfile } = this.props;
    if (!userId || isMeProfile) {
      return null;
    }
    return (
      <UserStatus>
        <UserStatusIndicator
          userId={userId}
          liveStreamType={LIVESTREAM_TYPE_DOT}
          onlineType={ONLINE_TYPE_BIGGER_DOT}
          shouldShowLiveStream
          shouldShowOnline
          livestreamSize={16}
        />
      </UserStatus>
    );
  };
  renderUsernameWithStatus = () => {
    const { username } = this.props;
    return (
      <UsernameWithStatus>
        <Username>{username}</Username>
        {this.renderUserStatusIndicator()}
      </UsernameWithStatus>
    );
  };
  renderMore = () => {
    const { meId, userId, isMeProfile, isProfileMoreModalOpen, toggleModal } =
      this.props;
    return (
      <MoreButton
        isMatch={isProfileMoreModalOpen}
        onClick={() => {
          this.trackUserClick({
            buttonId: ButtonId.All.ButtonMore,
          });
          return toggleModal({
            modalId: 'ProfileMoreModal',
            payload: {
              userId: isMeProfile ? meId : userId,
              features: [
                ...(!isMeProfile ? [UserMoreFeature.BLOCK] : []),
                canShare ? UserMoreFeature.SHARE : UserMoreFeature.COPY,
              ],
            },
            isOpen: true,
          });
        }}
      >
        <img src={MoreIconSource} alt="More" />
      </MoreButton>
    );
  };
  renderAvatar = () => {
    const {
      meId,
      userId,
      latestMessageId,
      latestMessageType,
      isMeProfile,
      canCreateStory,
      isLiveStreaming,
      addModal,
    } = this.props;
    const isHaloDisplayed =
      (latestMessageType === 'story' && !!latestMessageId) || isLiveStreaming;
    const shouldShowAdd = isMeProfile && !isHaloDisplayed;
    const renderAvatar = size => {
      return (
        <>
          <UniversalContainer
            filename="UserAvatarWithStatus"
            id={isMeProfile ? meId : userId}
            size={size}
            shouldUseModal
          />
          {shouldShowAdd && (
            <CreateWrapper
              onClick={() => {
                if (!canCreateStory) {
                  return addModal({
                    id: 'BecomeCreatorModal',
                  });
                }
              }}
            >
              <CreateButton>
                <AddIcon src={AddIconSource} />
              </CreateButton>
              {canCreateStory && <UploadStoryFileInput />}
            </CreateWrapper>
          )}
        </>
      );
    };
    return (
      <>
        <AvatarWrapper>{renderAvatar(AVATAR_SIZE)}</AvatarWrapper>
        <MobileAvatarWrapper>
          {renderAvatar(MOBILE_AVATAR_SIZE)}
        </MobileAvatarWrapper>
        <ModalView
          id="UserAvatarModal"
          render={() => (
            <UniversalContainer
              filename="UserAvatarModal"
              id={isMeProfile ? meId : userId}
            />
          )}
        />
      </>
    );
  };
  renderMetadata = () => {
    const { postCount, followerCount, followingCount, isMeProfile, t } =
      this.props;
    return (
      <MetadatasWrapper>
        <Metadata>
          <MetadataValue>{formatNumber(postCount)}</MetadataValue>
          <MetadataTitle>{t('posts')}</MetadataTitle>
        </Metadata>
        <Metadata
          as={isMeProfile ? Link : undefined}
          to="/profile/follower"
          onClick={() => {
            if (!isMeProfile) {
              return;
            }
            this.trackUserClick({
              buttonId: ButtonId.Profile.ButtonFollowers,
            });
          }}
        >
          <MetadataValue>{formatNumber(followerCount)}</MetadataValue>
          <MetadataTitle>{t('followers')}</MetadataTitle>
        </Metadata>
        <Metadata
          as={isMeProfile ? Link : undefined}
          to="/profile/following"
          onClick={() => {
            if (!isMeProfile) {
              return;
            }
            this.trackUserClick({
              buttonId: ButtonId.Profile.ButtonFollowing,
            });
          }}
        >
          <MetadataValue>{formatNumber(followingCount)}</MetadataValue>
          <MetadataTitle>{t('followings')}</MetadataTitle>
        </Metadata>
      </MetadatasWrapper>
    );
  };
  renderLocationBadge = () => {
    const { meId, userId, isMeProfile } = this.props;
    return (
      <Location>
        <LocationBadge
          userId={isMeProfile ? meId : userId}
          iconSize={16}
          fontSize={12}
        />
      </Location>
    );
  };
  renderLanguages = () => {
    const { languageBadges, t } = this.props;
    const languageString = languageBadges
      .filter(badge => 'lang:18+' !== badge)
      .map(badge => badge.replace(/lang:([\w]+)/, '$1'))
      .map(language => ('zh' === language ? 'zh_hant' : language))
      .map(language => t(`language_${language}`))
      .join(', ');
    return <Language>{languageString}</Language>;
  };
  renderUserActionButtons = () => {
    const { userId, subscriptionProductIds, t } = this.props;
    const hasSubscriptionPlan = subscriptionProductIds.length > 0;
    return (
      <>
        <FollowUserActionButton>
          <FollowButton userId={userId} isOnProfile isBigSize />
        </FollowUserActionButton>
        <MessageUserActionButton onClick={this.sendChatStart}>
          <img src={ChatIconSource} />
          {t('message')}
        </MessageUserActionButton>
        {this.renderUserSharableButton()}
        <SubscribeUserActionButton hasSubscriptionPlan={hasSubscriptionPlan}>
          <SubscribeButton userId={userId} isOnProfile isIntersected />
        </SubscribeUserActionButton>
      </>
    );
  };
  renderMeActionButtons = () => {
    const { hasUnsetItem, t } = this.props;
    return (
      <>
        <ActionButton
          as={Link}
          to={`/manage-profile/${ManageProfileType.ACCOUNT}`}
          onClick={() => {
            this.trackUserClick({
              buttonId: ButtonId.Profile.ButtonManageProfile,
            });
          }}
        >
          {t('manage_profile')}
          {hasUnsetItem && <RedDot />}
        </ActionButton>
        {this.renderMeSharableButton()}
      </>
    );
  };
  renderUserSharableButton = () => {
    const { t } = this.props;
    const shouldRender = !isChinaPartner && isClient;

    if (!shouldRender) {
      return null;
    }

    // Mobile: show `share` first; Show `copy` while `share` not supported
    // Desktop: always show `copy`
    if (isMobile && navigator.share) {
      return (
        <ShareUserActionButton onClick={this.shareProfile}>
          <img src={ShareIconSource} />
          {t('share')}
        </ShareUserActionButton>
      );
    }

    return navigator.clipboard ? (
      <ShareUserActionButton onClick={this.copyProfileLink}>
        <img src={LinkIconSource} />
        {t('general_copy')}
      </ShareUserActionButton>
    ) : null;
  };
  renderMeSharableButton = () => {
    const { t, isCP } = this.props;
    const shouldRender = !isChinaPartner && isClient && isCP;

    if (!shouldRender) {
      return null;
    }

    // Mobile: show `share` first; Show `copy` while `share` not supported
    // Desktop: always show `copy`
    if (isMobile && navigator.share) {
      return (
        <ActionButton onClick={this.shareProfile}>
          <img src={ShareIconSource} />
          {t('share_my_profile')}
        </ActionButton>
      );
    }

    return navigator.clipboard ? (
      <ActionButton onClick={this.copyProfileLink}>
        <img src={LinkIconSource} />
        {t('general_copy')}
      </ActionButton>
    ) : null;
  };
  renderJoinProgressBadge = () => {
    const { isMeProfile } = this.props;
    if (isMeProfile) {
      return (
        <JoinBadge>
          <JoinProgressBadge badgeType={JoinProgressBadgeType.SIMPLE} />
        </JoinBadge>
      );
    }
    return null;
  };
  componentDidMount() {
    this.fetchFollowStatus();
    this.fetchFeedsTotalCount();
    this.fetchUser();
    this.fetchShortLink();
  }
  componentDidUpdate(prevProps) {
    const { meId, userId } = this.props;
    const isMeIdChanged = meId !== prevProps.meId;
    const isUserIdChanged = userId !== prevProps.userId;
    if (isMeIdChanged || isUserIdChanged) {
      this.fetchFollowStatus();
      this.fetchFeedsTotalCount();
      this.fetchUser();
      this.fetchShortLink();
    }
  }
  render() {
    const {
      meId,
      userId,
      username,
      displayName,
      biography,
      originalBiography,
      isMeProfile,
      isCP,
      hasSwaggerBadge,
      badges,
      forbiddenWords,
      subscriptionProductIds,
      t,
    } = this.props;

    const { shouldShowTranslatedBiography, isBiographyTruncated } = this.state;
    const displayBiography = shouldShowTranslatedBiography
      ? biography
      : originalBiography;
    const hasSubscriptionPlan = subscriptionProductIds.length > 0;
    return (
      <StyledProfileInfo>
        <Info isMe={isMeProfile}>
          {this.renderUsernameWithStatus()}
          {this.renderAvatar()}
          {this.renderMetadata()}
          <InfoActionButtons>
            {isMeProfile
              ? this.renderMeActionButtons()
              : this.renderUserActionButtons()}
          </InfoActionButtons>
          {this.renderMore()}
        </Info>
        {!isMeProfile && (
          <UserActionButtons hasSubscriptionPlan={hasSubscriptionPlan}>
            {this.renderUserActionButtons()}
          </UserActionButtons>
        )}
        <NameWithBadge>
          <DisplayName>
            {displayName ? displayName : username ? username : '-'}
          </DisplayName>
          <UserLevelBadgeWrapper>
            <UserLevelBadge
              fontSize={10}
              height={14}
              shouldDirectToInfomation={true}
              userId={isMeProfile ? meId : userId}
            />
          </UserLevelBadgeWrapper>
          {(isCP || hasSwaggerBadge) && <CreatorIcon src={CreatorIconSource} />}
        </NameWithBadge>
        {this.renderJoinProgressBadge()}
        {this.renderLocationBadge()}
        {this.renderOnlineStatusWording()}
        {this.renderLanguages()}
        {badges.length > 0 && (
          <Badges isBiographyTruncated={isBiographyTruncated}>
            <ProfileInfoBadges userId={isMeProfile ? meId : userId} />
          </Badges>
        )}
        <Biography
          ref={this.setBiographyRef}
          isBiographyTruncated={isBiographyTruncated}
          onClick={() => {
            if (isBiographyTruncated) {
              this.trackUserClick({
                buttonId: ButtonId.All.ButtonExpand,
              });
              this.setState({ isBiographyTruncated: false });
            }
          }}
        >
          <LinkSubstring>
            {wordsFilter({
              value: displayBiography,
              filterRefString: forbiddenWords,
            })}
          </LinkSubstring>
        </Biography>
        {isBiographyTruncated && displayBiography ? (
          <MoreBiographyButton
            onClick={() => {
              if (isBiographyTruncated) {
                this.trackUserClick({
                  buttonId: ButtonId.All.ButtonExpand,
                });
                this.setState({ isBiographyTruncated: false });
              }
            }}
          >
            ... {t('view_more')}
          </MoreBiographyButton>
        ) : null}
        {displayBiography ? (
          <TranslationToggler
            onClick={() => {
              this.trackUserClick({
                buttonId: shouldShowTranslatedBiography
                  ? ButtonId.All.ButtonTextOriginal
                  : ButtonId.All.ButtonTextTranslate,
              });
              this.setState({
                shouldShowTranslatedBiography: !shouldShowTranslatedBiography,
              });
            }}
          >
            {t(
              shouldShowTranslatedBiography ? 'show_original' : 'show_translate'
            )}
          </TranslationToggler>
        ) : null}
        {isMeProfile && (
          <ActionButtons>{this.renderMeActionButtons()}</ActionButtons>
        )}
      </StyledProfileInfo>
    );
  }
}

ProfileInfo.propTypes = {
  intersectedFeedName: PropTypes.string,
  meId: PropTypes.string,
  userId: PropTypes.string,
  shareLink: PropTypes.string,
  shortShareLink: PropTypes.string,
  username: PropTypes.string,
  displayName: PropTypes.string,
  biography: PropTypes.string,
  originalBiography: PropTypes.string,
  forbiddenWords: PropTypes.string,
  latestMessageType: PropTypes.string,
  latestMessageId: PropTypes.string,
  postCount: PropTypes.number,
  followerCount: PropTypes.number,
  followingCount: PropTypes.number,
  lastModifiedAtUnix: PropTypes.number,
  isMeProfile: PropTypes.bool,
  isFollowing: PropTypes.bool,
  isCP: PropTypes.bool,
  canCreateStory: PropTypes.bool,
  hasUnsetItem: PropTypes.bool,
  isLiveStreaming: PropTypes.bool,
  isOnline: PropTypes.bool,
  isProfileMoreModalOpen: PropTypes.bool,
  hasSwaggerBadge: PropTypes.bool,
  badges: PropTypes.array,
  languageBadges: PropTypes.array,
  subscriptionProductIds: PropTypes.array,
  filters: PropTypes.array,
  sorts: PropTypes.array,
  copyTextWithToastr: PropTypes.func,
  sendFollowUser: PropTypes.func,
  sendUnfollowUser: PropTypes.func,
  sendChatStart: PropTypes.func,
  fetchFollowingTotalCount: PropTypes.func,
  fetchFollowersTotalCount: PropTypes.func,
  fetchUserFollowersTotalCount: PropTypes.func,
  fetchUserFollowingTotalCount: PropTypes.func,
  fetchUserIsFollowing: PropTypes.func,
  fetchFeedsTotalCount: PropTypes.func,
  fetchUser: PropTypes.func,
  fetchUserIsBlocked: PropTypes.func,
  fetchShortLink: PropTypes.func,
  fetchMeCreator: PropTypes.func,
  addModal: PropTypes.func,
  toggleModal: PropTypes.func,
  t: PropTypes.func.isRequired,
};

ProfileInfo.defaultProps = {
  intersectedFeedName: null,
  meId: null,
  userId: null,
  shareLink: null,
  shortShareLink: null,
  username: null,
  displayName: null,
  biography: null,
  originalBiography: null,
  forbiddenWords: '',
  latestMessageType: null,
  latestMessageId: null,
  postCount: 0,
  followerCount: 0,
  followingCount: 0,
  lastModifiedAtUnix: null,
  isMeProfile: false,
  isFollowing: null,
  isCP: false,
  canCreateStory: false,
  hasUnsetItem: false,
  isLiveStreaming: false,
  isOnline: false,
  isProfileMoreModalOpen: false,
  hasSwaggerBadge: false,
  badges: [],
  languageBadges: [],
  subscriptionProductIds: [],
  filters: [],
  sorts: [],
  copyTextWithToastr: () => null,
  sendFollowUser: () => null,
  sendUnfollowUser: () => null,
  sendChatStart: () => null,
  fetchFollowingTotalCount: () => null,
  fetchFollowersTotalCount: () => null,
  fetchUserFollowersTotalCount: () => null,
  fetchUserFollowingTotalCount: () => null,
  fetchUserIsFollowing: () => null,
  fetchFeedsTotalCount: () => null,
  fetchUser: () => null,
  fetchUserIsBlocked: () => null,
  fetchShortLink: () => null,
  fetchMeCreator: () => null,
  addModal: () => null,
  toggleModal: () => null,
};

const StyledProfileInfo = styled.div``;

const Info = styled.div`
  margin-bottom: 28px;
  display: grid;
  align-items: flex-start;
  grid-template-columns: ${AVATAR_SIZE}px 1fr 40px;
  grid-column-gap: 24px;
  grid-template-areas:
    'avatar username more-button'
    'avatar metadata metadata'
    'avatar action-buttons action-buttons';
  ${media.mobile`
    margin-bottom: 20px;
    align-items: center;
    grid-template-columns: ${MOBILE_AVATAR_SIZE}px 1fr;
    grid-template-areas: 'avatar metadata';
    grid-column-gap: 16px;
  `};
`;

const AvatarWrapper = styled.div`
  position: relative;
  margin-right: 16px;
  flex: none;
  grid-area: avatar;
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
  ${media.mobile`
    display: none;
  `};
`;

const MobileAvatarWrapper = styled(AvatarWrapper)`
  display: none;
  width: ${MOBILE_AVATAR_SIZE}px;
  height: ${MOBILE_AVATAR_SIZE}px;
  ${media.mobile`
    display: block;
  `};
`;

const CreateWrapper = styled.div`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const CreateButton = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  bottom: 4px;
  right: -4px;
  border-radius: 50%;
  border: 1px solid ${color.black};
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${color.tealBlue};
`;

const AddIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const MetadatasWrapper = styled.div`
  margin-bottom: 16px;
  flex: auto;
  grid-area: metadata;
  display: flex;
  justify-content: flex-start;
  ${media.mobile`
    margin-bottom: 0px;
    justify-content: space-around;
  `};
`;

const Metadata = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  & + & {
    margin-left: 16px;
  }
  ${media.mobile`
    flex-direction: column;
    justify-content: center;
    & + & {
      margin-left: 0px;
    }
  `};
`;

const MetadataValue = styled.div`
  margin-right: 10px;
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: ${textColor.white};
  text-align: center;
  line-height: 1;
  ${media.mobile`
    margin-right: 0px;
    margin-bottom: 4px;
  `};
`;

const MetadataTitle = styled.div`
  font-size: 16px;
  color: ${textColor.whtie};
  text-align: center;
  line-height: 1;
  ${media.mobile`
    font-size: 12px;
  `};
`;

const InfoActionButtons = styled.div`
  grid-area: action-buttons;
  display: flex;
  align-items: center;
  ${media.mobile`
    display: none;
  `};
`;

const NameWithBadge = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  font-size: 16px;
  color: ${textColor.white};
`;

const DisplayName = styled.div`
  margin-right: 4px;
  font-size: 20px;
  ${media.mobile`
    font-size: 16px;
  `};
`;

const UserLevelBadgeWrapper = styled.div`
  margin-right: 4px;
`;

const CreatorIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const JoinBadge = styled.div`
  margin-bottom: 12px;
`;

const Biography = styled.div`
  ${TextEllipsis};
  display: inline-block;
  font-size: 16px;
  line-height: 1.43;
  cursor: ${({ isBiographyTruncated }) =>
    isBiographyTruncated ? 'pointer' : 'auto'};
  max-height: ${({ isBiographyTruncated }) =>
    isBiographyTruncated ? `${16 * 1.43 * 3}px` : 'none'};
  color: ${textColor.white};
  white-space: pre-line;
  :empty {
    display: none;
  }
  ${media.mobile`
    font-size: 14px;
    max-height: ${({ isBiographyTruncated }) =>
      isBiographyTruncated ? `${14 * 1.43 * 3}px` : 'none'};
  `};
`;

const MoreBiographyButton = styled.button`
  ${ResetButtonStyle};
  display: block;
  font-size: 16px;
  line-height: 1.43;
  font-weight: 600;
  color: rgba(221, 221, 221, 0.4);
  ${media.mobile`
    font-size: 14px;
  `};
`;

const TranslationToggler = styled.button`
  ${ResetButtonStyle}
  display: block;
  margin-top: 12px;
  font-size: 16px;
  font-weight: 600;
  color: ${color.neutral[100]};
  line-height: 1.4;
  ${media.mobile`
    font-size: 12px;
  `};
`;

const ActionButtons = styled.div`
  display: none;
  ${media.mobile`
    margin-top: 20px;
    display: flex;
    align-items: center;
  `};
`;

const ActionButton = styled.button.attrs(
  ({ backgroundColor, borderColor, fontColor, opacity = 1 }) => ({
    style: {
      borderColor: borderColor ? borderColor : color.white,
      backgroundColor: backgroundColor ? backgroundColor : 'unset',
      color: fontColor ? fontColor : textColor.white,
      opacity,
    },
  })
)`
  ${ResetButtonStyle};
  border-radius: 4px;
  border-width: 1px;
  border-style: solid;
  padding-left: 12px;
  padding-right: 12px;
  height: 36px;
  min-width: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  & + & {
    margin-left: 8px;
  }
  > img {
    margin-right: 4px;
    width: 20px;
    height: 20px;
  }
  ${media.mobile`
    min-width: auto;
    height: 28px;
    flex: 1;
  `}
`;

const UserActionButtons = styled.div`
  display: none;
  ${media.mobile`
    margin-bottom: 16px;
    align-items: center;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 8px;
    grid-row-gap: 12px;
    ${({ hasSubscriptionPlan }) => {
      if (hasSubscriptionPlan) {
        return css`
          grid-template-areas:
            'follow-button message-button share-button'
            'subscribe-button subscribe-button subscribe-button';
        `;
      }
      return css`
        grid-template-areas: 'follow-button message-button share-button';
      `;
    }}
  `};
`;

const UserActionButton = styled(ActionButton)`
  height: 36px;
  font-size: 16px;

  & + & {
    margin-left: 0px;
  }
`;

const FollowUserActionButton = styled.div`
  margin-right: 12px;
  min-width: 160px;
  grid-area: follow-button;
  ${media.mobile`
    margin-right: 0px;
    min-width: auto;
  `};
`;

const SubscribeUserActionButton = styled.div.attrs(
  ({ hasSubscriptionPlan }) => ({
    style: {
      display: hasSubscriptionPlan ? 'block' : 'none',
    },
  })
)`
  min-width: 160px;
  grid-area: subscribe-button;
  ${media.mobile`
    min-width: auto;
  `};
`;

const MessageUserActionButton = styled(UserActionButton)`
  margin-right: 12px;
  grid-area: message-button;
  ${media.mobile`
    margin-right: 0px;
  `};
`;

const ShareUserActionButton = styled(UserActionButton)`
  margin-right: 12px;
  grid-area: share-button;
  ${media.mobile`
    margin-right: 0px;
  `};
`;

const RedDot = styled.span`
  margin-left: 8px;
  border-radius: 50%;
  width: 6px;
  height: 6px;
  background-color: ${color.red};
`;

const Badges = styled.div.attrs(({ isBiographyTruncated }) => ({
  style: {
    display: isBiographyTruncated ? '-webkit-box' : 'block',
  },
}))`
  margin-bottom: 8px;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Location = styled.div`
  margin-bottom: 8px;
  color: ${textColor.grey};
`;

const Language = styled.div`
  margin-bottom: 16px;
  font-size: 16px;
  color: ${textColor.grey};
  :empty {
    display: none;
  }
  ${media.mobile`
    font-size: 14px;
  `};
`;

const UserOnlineStatusWording = styled.div.attrs(
  ({ isOnline, isLiveStreaming }) => ({
    style: {
      color: isLiveStreaming
        ? color.carmine
        : isOnline
        ? color.lime
        : textColor.grey,
    },
  })
)`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  font-size: 16px;
  > div {
    margin-left: 4px;
  }
  ${media.mobile`
    font-size: 12px;
  `};
`;

const UsernameWithStatus = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  color: ${textColor.white};
  ${media.mobile`
    display: none;
  `};
`;

const Username = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin: 0;
`;

const UserStatus = styled.div`
  margin-left: 8px;
  flex: none;
`;

const MoreButton = styled.button`
  ${ResetButtonStyle};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  grid-area: more-button;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.4s;
  background-color: ${({ isMatch }) =>
    isMatch ? 'rgba(0, 126, 114, 0.2)' : 'transparent'};
  > img {
    width: 32px;
    height: 32px;
    object-fit: contain;
  }
  @media (hover: hover) {
    :hover {
      background-color: rgba(155, 155, 155, 0.1);
    }
  }
  ${media.mobile`
    display: none;
  `};
`;

export default withTranslation()(withFetchTranslation(ProfileInfo));
